import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Stack,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  Typography,
  CircularProgress,
  Button,
  Grid,
  Container,
  Box,
  Tabs,
  Tab,
  TextField,
  Chip,
  Paper,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import CustomPagination from 'src/components/CustomPagination';
import { loanService } from 'src/services/loan.service';
import Scrollbar from 'src/components/Scrollbar';
import Page from 'src/components/Page';
import Iconify from 'src/components/Iconify';
import SearchNotFound from 'src/components/SearchNotFound';
import { useAuth } from 'src/context/auth/AuthContext';
import { useViewScope } from 'src/hooks/useViewScope';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { formatScholarDollars, calculateLoanProgress } from 'src/utils/loanCalculator';

export default function LoanRequests() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // The shell decides labels; the view scope decides which loans are listed.
  const isAdmin = location.pathname.startsWith('/admin');
  const { ownerFilterId: teacherUserId } = useViewScope('students');
  const baseRoute = isAdmin ? '/admin' : '/teacher';

  useHeaderBreadcrumbs({
    breadcrumbs: [
      { label: isAdmin ? 'School Lending & Credit' : 'Classroom Lending & Credit', path: `${baseRoute}/loan-requests` },
      { label: 'Loan Portfolio & Queue' },
    ],
  });

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [tabIndex, setTabIndex] = useState<string>('ALL');
  const [selectedGrade, setSelectedGrade] = useState<string>('ALL');

  // 1. Fetch Portfolio Metrics
  const { data: metrics } = useQuery({
    queryKey: ['loan-portfolio-metrics', user?.id, isAdmin, teacherUserId],
    queryFn: () =>
      loanService.getLoanPortfolioSummary({
        teacherUserId,
        scope: isAdmin ? 'all' : 'assigned',
      }),
  });

  // 2. Fetch Loans Queue
  const {
    data: loansData,
    isLoading,
  } = useQuery({
    queryKey: [
      'teacher-loans',
      { page, rowsPerPage, searchText, tabIndex, selectedGrade, userId: user?.id, isAdmin, teacherUserId },
    ],
    queryFn: () =>
      loanService.getLoans({
        teacherUserId,
        scope: isAdmin ? 'all' : 'assigned',
        page,
        limit: rowsPerPage,
        search: searchText,
        status: tabIndex === 'ALL' ? undefined : tabIndex,
        grade: selectedGrade === 'ALL' ? undefined : selectedGrade,
      }),
  });

  // 3. Fetch All for Tab counts
  const { data: allTeacherLoans } = useQuery({
    queryKey: ['teacher-all-loans-counts', user?.id, isAdmin, teacherUserId],
    queryFn: () =>
      loanService.getLoans({
        teacherUserId,
        scope: isAdmin ? 'all' : 'assigned',
        limit: 1000,
      }),
  });

  const loans = loansData?.data || [];
  const allLoans = allTeacherLoans?.data || [];

  const counts = useMemo(() => {
    return {
      all: allLoans.length,
      pending: allLoans.filter((l) => l.status === 'Pending').length,
      active: allLoans.filter((l) => l.status === 'Approved').length,
      overdue: allLoans.filter((l) => l.status === 'Overdue').length,
      paid: allLoans.filter((l) => l.status === 'Paid').length,
      denied: allLoans.filter((l) => l.status === 'Denied').length,
    };
  }, [allLoans]);

  return (
    <Page title="Loan Requests & Portfolio">
      <Container maxWidth={false} sx={{ py: 2.5, px: { xs: 2, md: 3 } }}>
        {/* Top Header Row with Icon Box */}
        <Box sx={{ mb: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon="solar:bill-list-bold-duotone" width={24} height={24} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', fontSize: '1.375rem' }}>
                  {isAdmin ? 'Schoolwide Lending & Credit Portfolio' : 'Classroom Lending & Credit Hub'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                  Review student loan applications, evaluate creditworthiness, and monitor active loan repayments.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Box>

        {/* Portfolio KPI Stat Cards */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {/* Card 1: Total Capital Lent */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                  Total Credit Issued
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', my: 0.25, fontSize: '1.25rem' }}>
                  {formatScholarDollars(metrics?.totalLent || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  {metrics?.totalLoans || 0} Total Applications
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.75,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon="solar:wallet-money-bold-duotone" width={20} />
              </Box>
            </Card>
          </Grid>

          {/* Card 2: Total Outstanding Balance */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                  Outstanding Balance
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main', my: 0.25, fontSize: '1.25rem' }}>
                  {formatScholarDollars(metrics?.totalOutstanding || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  {metrics?.activeLoansCount || 0} Active Loans
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.75,
                  bgcolor: 'warning.lighter',
                  color: 'warning.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon="solar:hand-money-bold-duotone" width={20} />
              </Box>
            </Card>
          </Grid>

          {/* Card 3: Total Repaid & Recovery Rate */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                  Total Repaid
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main', my: 0.25, fontSize: '1.25rem' }}>
                  {formatScholarDollars(metrics?.totalPaid || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, fontSize: '0.6875rem' }}>
                  {metrics?.repaymentRate || 100}% Recovery Rate
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.75,
                  bgcolor: 'success.lighter',
                  color: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon="solar:shield-check-bold-duotone" width={20} />
              </Box>
            </Card>
          </Grid>

          {/* Card 4: Pending Review Queue */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}>
                  Pending Review
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main', my: 0.25, fontSize: '1.25rem' }}>
                  {metrics?.pendingLoansCount || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                  {metrics?.overdueLoansCount || 0} Overdue Loans
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.75,
                  bgcolor: 'info.lighter',
                  color: 'info.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon="solar:bill-list-bold-duotone" width={20} />
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Main Loan Queue Table */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {/* Controls Bar: Tabs + Filters */}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} sx={{ px: 2, py: 1 }}>
              <Tabs
                value={tabIndex}
                onChange={(_, val) => {
                  setTabIndex(val);
                  setPage(1);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 42,
                  '& .MuiTabs-indicator': {
                    bgcolor: 'primary.main',
                    height: 2.5,
                    borderRadius: 1.25,
                  },
                  '& .MuiTab-root': {
                    minHeight: 42,
                    py: 0.5,
                    px: 1.5,
                    borderRadius: 1.5,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    color: 'text.secondary',
                    minWidth: 'auto',
                    mr: 0.5,
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  },
                }}
              >
                <Tab
                  value="ALL"
                  icon={<Iconify icon="solar:layers-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>All Applications</span>
                      <Chip
                        label={counts.all}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'ALL' ? 'primary.main' : 'action.selected',
                          color: tabIndex === 'ALL' ? 'primary.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value="Pending"
                  icon={<Iconify icon="solar:hourglass-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>Pending Review</span>
                      <Chip
                        label={counts.pending}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'Pending' ? 'warning.main' : 'action.selected',
                          color: tabIndex === 'Pending' ? 'warning.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value="Approved"
                  icon={<Iconify icon="solar:hand-money-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>Active</span>
                      <Chip
                        label={counts.active}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'Approved' ? 'primary.main' : 'action.selected',
                          color: tabIndex === 'Approved' ? 'primary.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value="Overdue"
                  icon={<Iconify icon="solar:danger-triangle-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>Overdue</span>
                      <Chip
                        label={counts.overdue}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'Overdue' ? 'error.main' : 'action.selected',
                          color: tabIndex === 'Overdue' ? 'error.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value="Paid"
                  icon={<Iconify icon="solar:verified-check-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>Paid</span>
                      <Chip
                        label={counts.paid}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'Paid' ? 'primary.main' : 'action.selected',
                          color: tabIndex === 'Paid' ? 'primary.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
                <Tab
                  value="Denied"
                  icon={<Iconify icon="solar:close-circle-bold-duotone" width={16} />}
                  iconPosition="start"
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>Declined</span>
                      <Chip
                        label={counts.denied}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: tabIndex === 'Denied' ? 'error.main' : 'action.selected',
                          color: tabIndex === 'Denied' ? 'error.contrastText' : 'text.secondary',
                        }}
                      />
                    </Stack>
                  }
                />
              </Tabs>

              {/* Filters */}
              <Stack direction="row" spacing={1} sx={{ pb: { xs: 1, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={selectedGrade}
                    onChange={(e) => {
                      setSelectedGrade(e.target.value);
                      setPage(1);
                    }}
                    sx={{ bgcolor: 'background.paper', borderRadius: 1.5, '& .MuiSelect-select': { py: 0.65, fontSize: '0.8125rem' } }}
                  >
                    <MenuItem value="ALL">All Grades</MenuItem>
                    <MenuItem value="3">Grade 3</MenuItem>
                    <MenuItem value="4">Grade 4</MenuItem>
                    <MenuItem value="5">Grade 5</MenuItem>
                    <MenuItem value="6">Grade 6</MenuItem>
                    <MenuItem value="7">Grade 7</MenuItem>
                    <MenuItem value="8">Grade 8</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  placeholder="Search scholar / purpose..."
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setPage(1);
                  }}
                  InputProps={{
                    startAdornment: <Iconify icon="solar:magnifer-linear" width={15} sx={{ color: 'text.secondary', mr: 0.75 }} />,
                  }}
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-input': { py: 0.65, fontSize: '0.8125rem' },
                  }}
                />
              </Stack>
            </Stack>
          </Box>

          {/* Table Container with Explicit Widths and Clean Vertical Alignment */}
          <Scrollbar>
            <TableContainer>
              <Table sx={{ minWidth: 900 }}>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ width: '22%', py: 1.25, px: 2, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Scholar Name
                    </TableCell>
                    <TableCell sx={{ width: '9%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Grade
                    </TableCell>
                    <TableCell sx={{ width: '20%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Loan Purpose
                    </TableCell>
                    <TableCell sx={{ width: '10%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Principal
                    </TableCell>
                    <TableCell sx={{ width: '11%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Term & Rate
                    </TableCell>
                    <TableCell sx={{ width: '11%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Installment
                    </TableCell>
                    <TableCell sx={{ width: '9%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ width: '11%', py: 1.25, px: 1.5, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Repayment
                    </TableCell>
                    <TableCell align="right" sx={{ width: '7%', py: 1.25, px: 2, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        <CircularProgress size={26} />
                      </TableCell>
                    </TableRow>
                  ) : loans.length > 0 ? (
                    loans.map((row) => {
                      const studentUser = row.student?.user;
                      const fullName = studentUser ? `${studentUser.firstname} ${studentUser.lastname}` : 'Scholar';
                      const progress = calculateLoanProgress(row);

                      return (
                        <TableRow hover key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          {/* Scholar */}
                          <TableCell sx={{ py: 1.25, px: 2, verticalAlign: 'middle' }}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Avatar
                                src={studentUser?.photourl || undefined}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  bgcolor: 'primary.main',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                }}
                              >
                                {studentUser?.firstname?.charAt(0) || 'S'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem' }}>
                                  {fullName}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                                  {studentUser?.email || ''}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* Grade */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem' }}>
                              Grade {row.student?.grade || '--'}
                            </Typography>
                          </TableCell>

                          {/* Purpose */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle' }}>
                            <Tooltip title={row.reason} arrow placement="top-start">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem', maxWidth: 180 }} noWrap>
                                {row.reason}
                              </Typography>
                            </Tooltip>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                              {moment(row.createdat).format('MMM D, YYYY')}
                            </Typography>
                          </TableCell>

                          {/* Principal */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8125rem' }}>
                              {formatScholarDollars(row.amount)}
                            </Typography>
                          </TableCell>

                          {/* Term & Rate */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                              {row.term_months} Mo @ {row.interest_rate}%
                            </Typography>
                          </TableCell>

                          {/* Installment */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8125rem' }}>
                              {formatScholarDollars(row.monthly_payment)} / mo
                            </Typography>
                          </TableCell>

                          {/* Status */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            <Chip
                              icon={
                                <Iconify
                                  icon={
                                    row.status === 'Approved'
                                      ? 'solar:check-circle-bold-duotone'
                                      : row.status === 'Paid'
                                      ? 'solar:verified-check-bold-duotone'
                                      : row.status === 'Denied'
                                      ? 'solar:close-circle-bold-duotone'
                                      : 'solar:hourglass-bold-duotone'
                                  }
                                  width={13}
                                />
                              }
                              label={row.status}
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 600,
                                fontSize: '0.725rem',
                                bgcolor:
                                  row.status === 'Approved'
                                    ? 'success.lighter'
                                    : row.status === 'Paid'
                                    ? 'info.lighter'
                                    : row.status === 'Denied'
                                    ? 'error.lighter'
                                    : 'warning.lighter',
                                color:
                                  row.status === 'Approved'
                                    ? 'success.main'
                                    : row.status === 'Paid'
                                    ? 'info.main'
                                    : row.status === 'Denied'
                                    ? 'error.main'
                                    : 'warning.main',
                              }}
                            />
                          </TableCell>

                          {/* Repayment Progress */}
                          <TableCell sx={{ py: 1.25, px: 1.5, verticalAlign: 'middle' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.75rem', display: 'block' }}>
                              {formatScholarDollars(progress.paidAmount)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                              ({progress.percentPaid}% paid)
                            </Typography>
                          </TableCell>

                          {/* Actions */}
                          <TableCell align="right" sx={{ py: 1.25, px: 2, verticalAlign: 'middle' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Iconify icon="solar:eye-bold-duotone" width={14} />}
                              onClick={() => navigate(`${baseRoute}/view-loan/${row.id}`)}
                              sx={{
                                height: 28,
                                px: 1.25,
                                borderRadius: 1.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderColor: 'divider',
                                color: 'text.primary',
                                '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) },
                              }}
                            >
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        <SearchNotFound message="No loan requests found matching criteria." />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Pagination */}
          {loansData && loansData.count > rowsPerPage && (
            <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <CustomPagination
                count={loansData.count}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(p) => setPage(p)}
                onRowsPerPageChange={(r) => {
                  setRowsPerPage(r);
                  setPage(1);
                }}
              />
            </Box>
          )}
        </Card>
      </Container>
    </Page>
  );
}
